import { Link } from "react-router-dom";
import type { GameSession } from "../../store/gameStore";
import Badge from "../ui/Badge";
import { Crown, ShieldCheck, Clock, ChevronRight } from "lucide-react";

function timeAgo(epochMs: number): string {
  const seconds = Math.floor((Date.now() - epochMs) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

const roleBorder: Record<string, string> = {
  owner: "border-l-primary",
  player: "border-l-secondary",
};

const roleGlow: Record<string, string> = {
  owner: "hover:shadow-primary/10",
  player: "hover:shadow-secondary/10",
};

const statusLabel: Record<string, { text: string; variant: "accent" | "primary" | "warning" | "error" | "muted" }> = {
  lobby: { text: "Lobby", variant: "primary" },
  active: { text: "Active", variant: "accent" },
  ended: { text: "Ended", variant: "warning" },
  done: { text: "Done", variant: "muted" },
  purged: { text: "Purged", variant: "error" },
};

export default function GameCard({ session }: { session: GameSession }) {
  return (
    <Link
      to={`/game/${session.gameId}`}
      className={`group block rounded-2xl border border-border border-l-4 ${roleBorder[session.role] ?? "border-l-border"} bg-surface-raised p-5 transition-all hover:-translate-y-1 hover:shadow-xl ${roleGlow[session.role] ?? ""}`}
    >
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <p className="truncate font-display text-xl font-extrabold text-text">
            {session.gameTitle}
          </p>
          <p className="mt-0.5 font-display text-sm font-bold tracking-widest text-primary uppercase">
            {session.slug}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <div className="flex gap-1">
            {(() => {
              const info = session.gameStatus ? statusLabel[session.gameStatus] : undefined;
              return info ? <Badge variant={info.variant}>{info.text}</Badge> : null;
            })()}
            <Badge variant={session.role === "owner" ? "primary" : "secondary"}>
              {session.role === "owner" && <Crown className="mr-1 inline h-3 w-3" />}
              {session.role}
            </Badge>
            {session.isModerator && session.role !== "owner" && (
              <Badge variant="accent">
                <ShieldCheck className="mr-1 inline h-3 w-3" />
                mod
              </Badge>
            )}
          </div>
          <ChevronRight className="h-5 w-5 text-text-secondary opacity-0 transition-opacity group-hover:opacity-100" />
        </div>
      </div>
      <div className="mt-3 flex items-center gap-3 text-sm text-text-secondary">
        {/* Avatar initial */}
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
          {session.displayName.charAt(0).toUpperCase()}
        </span>
        <span className="font-medium">{session.displayName}</span>
        <span className="text-border">|</span>
        <span className="inline-flex items-center gap-1">
          <Clock className="h-3.5 w-3.5" />
          {timeAgo(session.joinedAt)}
        </span>
      </div>
    </Link>
  );
}
